import {syncItemChangesDeep} from "@/plugins/formatting";
import {state as appStoreDialogState, getters as appStoreDialogGetters, flags} from "@/store/modules/AppStoreDialog/appStoreDialog";
import {actions as featureActions} from "@/store/modules/feature";
import {actions as appActions, state as appState} from "@/store/modules/app";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {actions as dialogActions} from "@/store/modules/dialog";
import {state as accountState, actions as accountActions} from "@/store/modules/account";
import {APP_STORE_APP_TYPES, APP_STORE_BUNDLE_TYPES} from "@/helpers/constants";
import {$t} from "@/i18n";
import {getManifestData} from "@/views/AppStore/app-store-manifest";
import router from "@/router";
import AppStoreModule from "@/modules/appStore/appStore";
import ApiAppStore from "@/api/app_store/appStore";
import {syncItemChanges} from "../../../plugins/formatting";
import {useLoading} from '../loading'

const {startLoading, finishLoading} = useLoading("plugins");

/**
 * Public methods
 */
export default function useAppStoreDialogActions() {

    return {
        openAppStoreModal,
        updateCurrentTab,
        updateCurrentTabWithDelay,
        install,
        subscribe,
        deactivate,
        disconnect,
        setTabs,
        setFooterActions,
        fetchAppData
    }

    // other methods should not be called until this method opens the modal
    async function openAppStoreModal(feature_name, account_id, {onUpdate, dialogOptions}) {
        setDialogCallback(onUpdate)

        await fetchAppData(feature_name, account_id)

        fetchUpgradeBundle()

        if (feature_name) {
            dialogActions.setDialogOptions({
                component: () => import("@/views/AppStore/AppStoreDialog/AppStoreDialogContent"),
                width: 920,
                payload: {
                    name: feature_name,
                    account_id: account_id,
                    isPersistent: true,
                    fixed: true,
                    scrollable: true,
                    ...(dialogOptions || {})
                }
            });
        }
    }

    function fetchUpgradeBundle() {
        let selectedAccount = appStoreDialogGetters.getSelectedCompany()
        let account_id = selectedAccount.id

        appActions.fetchClassifiers({types: ["bundles"], account_id: account_id})
        let bundles = appState.bundles

        if (bundles[account_id] === undefined) {
            appStoreDialogState.upgradeBundle = null
        } else if (bundles[account_id].length) {
            let team_bundle = bundles[account_id].find(x => x.name === APP_STORE_BUNDLE_TYPES.TEAM);
            let enterprise_bundle = bundles[account_id].find(x => x.name === APP_STORE_BUNDLE_TYPES.ENTERPRISE);

            if (team_bundle && selectedAccount.bundles.indexOf(team_bundle.id) > -1) {
                appStoreDialogState.upgradeBundle = enterprise_bundle;
            } else {
                appStoreDialogState.upgradeBundle = team_bundle;
            }
        }
    }

    function updateCurrentTab(value, currentTabOptions = {}) {
        setFooterActions([])
        appStoreDialogState.currentTab = value
        appStoreDialogState.currentTabOptions = currentTabOptions
    }

    function updateCurrentTabWithDelay(value, currentTabOptions = {}) {
        // UX feels better with timeout
        setTimeout(() => {
            updateCurrentTab(value, currentTabOptions)
        }, 300)
    }

    function install() {
        let titlePrefix = !appStoreDialogState.appData.is_activatable ? $t("Activate") : $t("Install")

        dialogActions.setConfirmOptions({
            title: "{0} - {1}".format(titlePrefix, getManifestData(appStoreDialogState.appData.name, "title")),
            text: getManifestData(appStoreDialogState.appData.name, "description"),
            confirm_text: $t("Confirm"),
            cancel_text: $t("Cancel"),
            action: () => activateFeature()
        });
        dialogActions.openConfirmDialog();
    }

    function deactivate() {
        let titlePrefix = !appStoreDialogState.appData.is_activatable ? $t("Unsubscribe from waiting list") : $t("Deactivate")

        dialogActions.setConfirmOptions({
            title: "{0} - {1}".format(titlePrefix, getManifestData(appStoreDialogState.appData.name, "title")),
            text: $t("You are about to deactivate this app."),
            confirm_text: $t("Confirm"),
            cancel_text: $t("Cancel"),
            action: () => deactivateFeature()
        });
        dialogActions.openConfirmDialog();
    }

    function disconnect() {

        dialogActions.setConfirmOptions({
            title: "{0} - {1}".format($t("Disconnect"), getManifestData(appStoreDialogState.appData.name, "title")),
            text: $t("You are about to disconnect this app."),
            confirm_text: $t("Confirm"),
            cancel_text: $t("Cancel"),
            action: () => disconnectFeature()
        });
        dialogActions.openConfirmDialog();
    }

    function subscribe() {
        const appData = appStoreDialogState.appData
        router.push({
            name: "subscription_choose_plan",
            params: {id: appData.account_id},
            query: {new_app: appData.id}
        });
        dialogActions.closeDialog()
    }

    function setTabs(tabs) {
        appStoreDialogState.tabs = tabs;
    }

    function setFooterActions(footerActions) {
        appStoreDialogState.footerActions = footerActions;
    }

    function setDialogCallback(callback) {
        appStoreDialogState.onUpdate = callback
    }

    async function deactivateFeature() {
        try {
            const appData = appStoreDialogState.appData
            let resp = await featureActions.execAppAction({
                account_id: appData.account_id,
                feature_id: appData.id,
                action: 'deactivate'
            })
            if (!appData.is_activatable) {
                handleSoftLaunchToggled(appData.name, 0)
            }
            handleDeactivateResponse(resp)
            dialogActions.closeDialog()
        } catch (e) {
            errorDialog.showError(e)
        }
    }

    function handleDeactivateResponse(response) {
        if (getIndexOfCurrentFeature() > -1) {
            getCurrentAccountInfo().features.splice(getIndexOfCurrentFeature(), 1);
        }
        settingsChanged(response)
    }

    async function disconnectFeature() {
        startLoading('disconnectOrDeactivateFeature')
        try {
            const appData = appStoreDialogState.appData
            let resp = await featureActions.execAppAction({
                account_id: appData.account_id,
                feature_id: appData.id,
                action: 'disconnect'
            })
            handleDisconnectResponse(resp)
        } catch (e) {
            errorDialog.showError(e)
        }
        finishLoading('disconnectOrDeactivateFeature')
    }

    function handleDisconnectResponse(response) {
        settingsChanged(response)
    }

    async function activateFeature(appData = null) {
        if (!appData) {
            appData = appStoreDialogState.appData
        }
        try {
            const resp = await featureActions.execAppAction({
                account_id: appData.account_id,
                feature_id: appData.id,
                action: 'activate'
            })
            handleInstallResponse(resp, appData)
        } catch (e) {
            errorDialog.showError(e)
        }
    }

    async function fetchAppData(feature_name, account_id) {
        if ([APP_STORE_APP_TYPES.TASKS, APP_STORE_APP_TYPES.CLIENTS, APP_STORE_APP_TYPES.COMPANY].includes(feature_name)) {
            appStoreDialogState.appData = {
                current_plan: {},
                name: feature_name,
                account_feature: {},
                account_feature_settings: {},
                account_id: account_id,
                feature_price: {},
                is_activatable: true
            }
        } else {
            await loadAppData(feature_name, account_id)
        }
    }

    async function loadAppData(feature_name, account_id) {
        try {
            let response = await featureActions.fetchApp({
                company_id: account_id,
                feature_id_or_name: feature_name
            })
            await appActions.fetchClassifiers({types: ["countries"]})
            setAppDataFromResponse(response, account_id)
        } catch (err) {
            errorDialog.showError(err)
        }
    }

    function setAppDataFromResponse(response, account_id) {
        appStoreDialogState.appData = response
        appStoreDialogState.appData.account_id = account_id
    }


    function handleInstallResponse(response, appData = null) {
        if (!appData) {
            appData = appStoreDialogState.appData
        }
        if (getIndexOfFeatureByName(appData.name) === -1 && appData.is_activatable) {
            getCurrentAccountInfo().features.push(appData.name);
        }
        settingsChanged(response)
        if (!appData.is_activatable) {
            handleSoftLaunchToggled(appData.name, 1)
        }
        handleInstallPerApp(appData.name)
    }

    async function handleInstallPerApp(name) {
        if (name === APP_STORE_APP_TYPES.BILLING) {
            updateCurrentTabWithDelay('setup')
        } else if (name === APP_STORE_APP_TYPES.ECONOMIC) {
            try {
                let billingApp = await featureActions.fetchApp({
                    company_id: appStoreDialogState.appData.account_id,
                    feature_id_or_name: APP_STORE_APP_TYPES.BILLING
                })

                billingApp.account_id = appStoreDialogState.appData.account_id
                if (!AppStoreModule.isAppActive(billingApp)) {
                    dialogActions.setConfirmOptions({
                        title: $t('Do you also want to activate billing to get the most out of Uku?'),
                        confirm_text: $t("Confirm"),
                        cancel_text: $t("Skip"),
                        action: async () => {
                            try {
                                await activateFeature(billingApp);
                                await ApiAppStore.execAppAction(
                                    billingApp.account_id,
                                    billingApp.id,
                                    "save_settings",
                                    {vendor: 'economic'}
                                )
                            } catch (e) {
                                errorDialog.showError(e)
                            }
                        }
                    });
                    dialogActions.openConfirmDialog();
                }
            } catch (e) {
                errorDialog.showError(e)
            }
        } else if ([APP_STORE_APP_TYPES.XERO, APP_STORE_APP_TYPES.QUICKBOOKS, APP_STORE_APP_TYPES.FORTNOX, APP_STORE_APP_TYPES.TRIPLETEX, APP_STORE_APP_TYPES.SMART_ACCOUNTS, APP_STORE_APP_TYPES.MERIT_AKTIVA].includes(name)) {
            try {
                let billingApp = await featureActions.fetchApp({
                    company_id: appStoreDialogState.appData.account_id,
                    feature_id_or_name: APP_STORE_APP_TYPES.BILLING
                })

                billingApp.account_id = appStoreDialogState.appData.account_id
                if (!AppStoreModule.isAppActive(billingApp)) {
                    dialogActions.setConfirmOptions({
                        title: $t('Do you also want to activate billing to get the most out of Uku?'),
                        confirm_text: $t("Confirm"),
                        cancel_text: $t("Skip"),
                        action: async () => {
                            try {
                                await activateFeature(billingApp);
                                await ApiAppStore.execAppAction(
                                    billingApp.account_id,
                                    billingApp.id,
                                    "save_settings",
                                    {vendor: name}
                                )
                            } catch (e) {
                                errorDialog.showError(e)
                            }
                        }
                    });
                    dialogActions.openConfirmDialog();
                }
            } catch (e) {
                errorDialog.showError(e)
            }
        }
    }

    function handleSoftLaunchToggled(feature_name, value) {
        const company = appStoreDialogGetters.getSelectedCompany()
        const _key = `is_${feature_name}_subscribed`
        company.data[_key] = value
        accountActions.updateAccountSettings(company)
    }

    function getIndexOfCurrentFeature() {
        const appData = appStoreDialogState.appData
        return getCurrentAccountInfo()
            .features
            .indexOf(appData.name)
    }

    function getIndexOfFeatureByName(feature_name) {
        return getCurrentAccountInfo()
            .features
            .indexOf(feature_name)
    }

    function getCurrentAccountInfo() {
        let id = appStoreDialogGetters.getAccountIdFromAppData()
        return accountState.accounts_info.find(acc => acc.id === id)
    }

    function settingsChanged(resp) {
        clearCacheFromResponse(resp)
        syncFeatureResponseChangesToList(resp)
        syncFeatureResponseChangesToAppData(resp)
    }

    function clearCacheFromResponse(response) {
        const appData = appStoreDialogState.appData
        if (response.clear_cache !== undefined) {
            response.clear_cache.forEach(key => appActions.clearClassifiersCache(key, appData.account_id))
        }
    }

    function syncFeatureResponseChangesToList(response) {
        const appData = appStoreDialogState.appData
        if (response.account_feature) {
            syncItemChanges(appData.account_feature, response.account_feature);
            callDialogCallback(response.account_feature)
        }
    }

    function callDialogCallback(feature) {
        if (appStoreDialogState.onUpdate) {
            appStoreDialogState.onUpdate(feature)
        }
    }

    function syncFeatureResponseChangesToAppData(response) {
        const appData = appStoreDialogState.appData
        if (response.account_feature_settings !== undefined) {
            if (appData.account_feature_settings == null)
                appData.account_feature_settings = response.account_feature_settings;
            else {
                syncItemChangesDeep(appData.account_feature_settings, response.account_feature_settings);
            }
        }

        // This is a hack to trigger reactivity.
        // When you don't have an account_feature, and you activate the app then the activate button is still visible
        if (appStoreDialogState.appData) {
            appStoreDialogState.appData = { ...appStoreDialogState.appData }
        }
    }
}